import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';

const API_URL = '/api';

const SSEContext = createContext(null);

export function SSEProvider({ auth, children }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef(null);

  // Initial fetch for notifications
  const fetchNotifications = useCallback(async () => {
    if (!auth?.token) return;
    try {
      const res = await axios.get(`${API_URL}/notifications`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      setNotifications(res.data?.notifications || []);
      setUnreadCount(res.data?.unreadCount || 0);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  }, [auth?.token]);

  // Connect to SSE stream
  useEffect(() => {
    if (!auth?.token) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setIsConnected(false);
      return;
    }

    // Initial load
    fetchNotifications();

    const sseUrl = `${API_URL}/events?token=${encodeURIComponent(auth.token)}`;
    const es = new EventSource(sseUrl);
    eventSourceRef.current = es;

    es.onopen = () => {
      setIsConnected(true);
    };

    es.addEventListener('connected', () => {
      setIsConnected(true);
    });

    es.addEventListener('comment_added', (e) => {
      try {
        const data = JSON.parse(e.data);
        // If there is an in-app notification for this user, prepend it and increment count
        if (data?.notification && data?.recipient_id === auth?.user?.id) {
          setNotifications((prev) => {
            const exists = prev.some((n) => n.id === data.notification.id);
            if (exists) return prev;
            return [data.notification, ...prev];
          });
          setUnreadCount((prev) => prev + 1);
        }

        // Dispatch a global event that any view (TrainingPlanTable, ClientDetail, etc.) can listen to
        window.dispatchEvent(new CustomEvent('tp:comment_added', { detail: data }));
      } catch (err) {
        console.error('Error parsing comment_added SSE event:', err);
      }
    });

    es.addEventListener('notification_updated', (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.action === 'read_all') {
          setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
          setUnreadCount(0);
        } else if (data.action === 'read_one') {
          setNotifications((prev) =>
            prev.map((n) => (n.id === data.id ? { ...n, isRead: true } : n))
          );
          setUnreadCount(data.unreadCount ?? 0);
        } else if (data.action === 'delete') {
          setNotifications((prev) => prev.filter((n) => n.id !== data.id));
          setUnreadCount(data.unreadCount ?? 0);
        }
      } catch (err) {
        console.error('Error parsing notification_updated SSE event:', err);
      }
    });

    es.addEventListener('workout_updated', (e) => {
      try {
        const data = JSON.parse(e.data);
        window.dispatchEvent(new CustomEvent('tp:workout_updated', { detail: data }));
      } catch (err) {
        console.error('Error parsing workout_updated SSE event:', err);
      }
    });

    es.addEventListener('workout_deleted', (e) => {
      try {
        const data = JSON.parse(e.data);
        window.dispatchEvent(new CustomEvent('tp:workout_deleted', { detail: data }));
      } catch (err) {
        console.error('Error parsing workout_deleted SSE event:', err);
      }
    });

    es.onerror = () => {
      setIsConnected(false);
      // Native EventSource automatically handles reconnection
    };

    // Re-sync notifications when tab becomes visible after being backgrounded
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchNotifications();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setIsConnected(false);
    };
  }, [auth?.token, auth?.user?.id, fetchNotifications]);

  const markAsRead = async (notificationId) => {
    if (!auth?.token) return;
    try {
      await axios.patch(`${API_URL}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    if (!auth?.token) return;
    try {
      await axios.post(`${API_URL}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  const deleteNotification = async (notificationId) => {
    if (!auth?.token) return;
    try {
      await axios.delete(`${API_URL}/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      const item = notifications.find((n) => n.id === notificationId);
      if (item && !item.isRead) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const value = {
    notifications,
    unreadCount,
    isConnected,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification
  };

  return <SSEContext.Provider value={value}>{children}</SSEContext.Provider>;
}

export function useSSE() {
  const context = useContext(SSEContext);
  return context || {};
}
